import {
  DocumentFormEnum,
  NevdisSecurityCheckEnum,
  BankSecurityCheckEnum,
  RegoSecurityCheckEnum,
  DocumentFormType
} from '@types';
import SecurityCheckCard from '../components/RightPanelCards/SecurityCheckCard';
import NevdisCheckCard from '../components/RightPanelCards/NevdisCheckCard';
import BankCheckCard from '../components/RightPanelCards/BankCheckCard';
import RegoCheckCard from '../components/RightPanelCards/RegoCheckCard';

/**
 * Maps panel/section names to rule types
 * Used in RightDetailsBar for determining which rule is associated with a panel
 */
export const panelToRuleMap: Record<string, string> = {
  // License checks from SECURITY_CHECK_LIST
  licenseExpiry: 'ID_EXPIRY',
  licenseFaceMatch: 'ID_FACE_MATCH',
  licenseLiveness: 'ID_LIVENESS',
  licenseAuthenticity: 'ID_AUTHENTICITY',
  // NEVDIS checks from SECURITY_CHECK_LIST
  ppsr: 'ASSET_PPSR_ENCUMBRANCE',
  stolen: 'ASSET_STOLEN',
  writtenOff: 'ASSET_WRITTEN_OFF',
  vin: 'ASSET_VIN_REGO_MATCH',
  registration: 'ASSET_REGISTRATION',
  vehiclePhotos: 'ASSET_AI_PHOTO_SCREEN',
  vehiclePhotosQuality: 'ASSET_PHOTOS_QUALITY',
  vehiclePhotosAuthenticity: 'ASSET_PHOTOS_AUTHENTICITY',
  // Registration checks from SECURITY_CHECK_LIST
  expiry: 'REGO_CERTIFICATE_EXPIRY_MATCH',
  registrationAuthenticity: 'REGO_CERTIFICATE_AUTHENTICITY',
  // Bank checks from SECURITY_CHECK_LIST
  bankAuthenticity: 'BANK_STATEMENT_AUTHENTICITY',
  BANK_STATEMENT_ADDRESS: 'BANK_STATEMENT_ADDRESS_MATCH',
  address: 'BANK_STATEMENT_ADDRESS_MATCH',
  // Additional mappings for panel types
  DRIVER_LICENCE_FRONT: 'ID_EXPIRY',
  VIN: 'ASSET_VIN_REGO_MATCH',
  regoPaper: 'REGO_CERTIFICATE_EXPIRY_MATCH',
  bank: 'BANK_STATEMENT_AUTHENTICITY',
  // DocumentFormEnum mappings (from InspectionFailurePanel)
  LICENSE_EXPIRY: 'ID_EXPIRY',
  LICENSE_FACE_MATCH: 'ID_FACE_MATCH',
  LICENSE_AUTHENTICITY: 'ID_AUTHENTICITY',
  LICENSE_LIVENESS: 'ID_LIVENESS',
  NEVDIS_SECURITY_STOLEN: 'ASSET_STOLEN',
  NEVDIS_SECURITY_WRITTEN_OFF: 'ASSET_WRITTEN_OFF',
  NEVDIS_SECURITY_VIN: 'ASSET_VIN_REGO_MATCH',
  NEVDIS_SECURITY_PPSR: 'ASSET_PPSR_ENCUMBRANCE',
  NEVDIS_SECURITY_REGISTRATION: 'ASSET_REGISTRATION',
  NEVDIS_SECURITY_PHOTO: 'ASSET_AI_PHOTO_SCREEN',
  REGO_SECURITY_EXPIRY: 'REGO_CERTIFICATE_EXPIRY_MATCH',
  REGO_SECURITY_AUTHENTICITY: 'REGO_CERTIFICATE_AUTHENTICITY',
  BANK_SECURITY_AUTHENTICITY: 'BANK_STATEMENT_AUTHENTICITY'
};

/**
 * Maps security check types to check type and condition
 * Used in SecurityCheck for displaying rule status tags
 */
export const securityCheckTypeMappings: Record<
  string,
  { checkType: string; condition: string }
> = {
  // License checks
  LICENSE_EXPIRY: {
    checkType: 'identification-id-expiry',
    condition: 'expired'
  },
  LICENSE_FACE_MATCH: {
    checkType: 'identification-face-match',
    condition: 'low-confidence'
  },
  LICENSE_AUTHENTICITY: {
    checkType: 'identification-document-authenticity',
    condition: 'low-confidence'
  },
  LICENSE_LIVENESS: {
    checkType: 'identification-liveness-check',
    condition: 'low-confidence'
  },
  // Vehicle/Asset checks
  NEVDIS_STOLEN: {
    checkType: 'asset-details-stolen-check',
    condition: 'stolen'
  },
  NEVDIS_WRITTEN_OFF: {
    checkType: 'asset-details-written-off-check',
    condition: 'written-off'
  },
  VEHICLE_VIN: {
    checkType: 'asset-details-vin-plate-match',
    condition: 'mismatch'
  },
  PPSR_ENCUMBRANCE: {
    checkType: 'asset-details-financial-interest',
    condition: 'registered-interest-found'
  },
  MARINE_PPSR_ENCUMBRANCE: {
    checkType: 'asset-details-financial-interest',
    condition: 'registered-interest-found'
  },
  NEVDIS_REGISTRATION: {
    checkType: 'asset-details-nevdis-registration-status',
    condition: 'expired'
  },
  VEHICLE_PHOTOS: {
    checkType: 'asset-details-ai-photo-check',
    condition: 'suspected-screen-photo'
  },
  VEHICLE_PHOTOS_QUALITY: {
    checkType: 'asset-details-ai-photo-check',
    condition: 'poor-quality'
  },
  VEHICLE_PHOTOS_AUTHENTICITY: {
    checkType: 'asset-details-ai-photo-check',
    condition: 'suspected-screen-photo'
  },
  AI_PHOTO_CHECK: {
    checkType: 'asset-details-ai-photo-check',
    condition: 'poor-quality'
  },
  // Registration certificate checks
  REGO_EXPIRY: {
    checkType: 'registration-certificate-expiry-date-match',
    condition: 'mismatch-with-nevdis'
  },
  REGO_CERTIFICATE: {
    checkType: 'registration-certificate-document-authenticity',
    condition: 'low-confidence'
  },
  // Bank statement checks
  BANK_STATEMENT: {
    checkType: 'bank-statement-document-authenticity',
    condition: 'low-confidence'
  },
  BANK_STATEMENT_ADDRESS: {
    checkType: 'bank-statement-address-match',
    condition: 'mismatch-with-registration-certificate'
  },
  // Additional mappings for vehiclePhotos - handle different type variations
  EXTERNAL_PHOTOS: {
    checkType: 'asset-details-ai-photo-check',
    condition: 'suspected-screen-photo'
  },
  NEVDIS_SECURITY_PHOTO: {
    checkType: 'asset-details-ai-photo-check',
    condition: 'suspected-screen-photo'
  },
  VEHICLEPHOTOS: {
    checkType: 'asset-details-ai-photo-check',
    condition: 'suspected-screen-photo'
  },
  // This is the correct mapping based on panelToRuleMap
  ASSET_AI_PHOTO_SCREEN: {
    checkType: 'asset-details-ai-photo-check',
    condition: 'suspected-screen-photo'
  }
};

/**
 * Maps rule types to their corresponding panel components and types
 * Used in InspectionFailurePanel for determining which component to render
 */
export const ruleTypeToConfig: Record<
  string,
  { component: any; type: DocumentFormType }
> = {
  // Identification related rules
  ID_EXPIRY: {
    component: SecurityCheckCard,
    type: DocumentFormEnum.DETAILS_DRIVER_LICENSE_EXPIRY
  },
  ID_FACE_MATCH: {
    component: SecurityCheckCard,
    type: DocumentFormEnum.DETAILS_DRIVER_LICENSE_FACE_MATCH
  },
  ID_AUTHENTICITY: {
    component: SecurityCheckCard,
    type: DocumentFormEnum.DETAILS_DRIVER_LICENSE_AUTHENTICITY
  },
  ID_LIVENESS: {
    component: SecurityCheckCard,
    type: DocumentFormEnum.DETAILS_DRIVER_LICENSE_LIVENESS
  },
  // Asset/NEVDIS related rules
  ASSET_STOLEN: {
    component: NevdisCheckCard,
    type: NevdisSecurityCheckEnum.NEVDIS_SECURITY_STOLEN
  },
  ASSET_WRITTEN_OFF: {
    component: NevdisCheckCard,
    type: NevdisSecurityCheckEnum.NEVDIS_SECURITY_WRITTEN_OFF
  },
  ASSET_VIN_REGO_MATCH: {
    component: NevdisCheckCard,
    type: NevdisSecurityCheckEnum.NEVDIS_SECURITY_VIN
  },
  ASSET_PPSR_ENCUMBRANCE: {
    component: NevdisCheckCard,
    type: NevdisSecurityCheckEnum.NEVDIS_SECURITY_PPSR
  },
  ASSET_REGISTRATION: {
    component: NevdisCheckCard,
    type: NevdisSecurityCheckEnum.NEVDIS_SECURITY_REGISTRATION
  },
  ASSET_PHOTOS_AUTHENTICITY: {
    component: NevdisCheckCard,
    type: NevdisSecurityCheckEnum.NEVDIS_SECURITY_PHOTO
  },
  ASSET_PHOTOS_QUALITY: {
    component: NevdisCheckCard,
    type: NevdisSecurityCheckEnum.NEVDIS_SECURITY_PHOTO
  },
  // Registration certificate rules
  REGO_CERTIFICATE_EXPIRY_MATCH: {
    component: RegoCheckCard,
    type: RegoSecurityCheckEnum.REGO_SECURITY_EXPIRY
  },
  REGO_CERTIFICATE_AUTHENTICITY: {
    component: RegoCheckCard,
    type: RegoSecurityCheckEnum.REGO_SECURITY_AUTHENTICITY
  },
  // Bank statement rules
  BANK_STATEMENT_AUTHENTICITY: {
    component: BankCheckCard,
    type: BankSecurityCheckEnum.BANK_SECURITY_AUTHENTICITY
  },
  BANK_STATEMENT_ADDRESS_MATCH: {
    component: BankCheckCard,
    type: BankSecurityCheckEnum.BANK_STATEMENT_ADDRESS
  }
};

/**
 * Maps rule types to their corresponding security check types and keys
 * Used in InspectionFailurePanel for opening the correct panel
 */
export const ruleTypeToSecurityCheck: Record<
  string,
  { key: string; type: string }
> = {
  ID_EXPIRY: { key: 'licenseExpiry', type: 'LICENSE_EXPIRY' },
  ID_FACE_MATCH: { key: 'licenseFaceMatch', type: 'LICENSE_FACE_MATCH' },
  ID_AUTHENTICITY: { key: 'licenseAuthenticity', type: 'LICENSE_AUTHENTICITY' },
  ID_LIVENESS: { key: 'licenseLiveness', type: 'LICENSE_LIVENESS' },
  ASSET_STOLEN: { key: 'stolen', type: 'NEVDIS_STOLEN' },
  ASSET_WRITTEN_OFF: { key: 'writtenOff', type: 'NEVDIS_WRITTEN_OFF' },
  ASSET_VIN_REGO_MATCH: { key: 'vin', type: 'VEHICLE_VIN' },
  ASSET_PPSR_ENCUMBRANCE: { key: 'ppsr', type: 'PPSR_ENCUMBRANCE' },
  ASSET_REGISTRATION: {
    key: 'registration',
    type: 'NEVDIS_REGISTRATION'
  },
  ASSET_PHOTOS_AUTHENTICITY: {
    key: 'vehiclePhotosAuthenticity',
    type: 'VEHICLE_PHOTOS_AUTHENTICITY'
  },
  ASSET_PHOTOS_QUALITY: {
    key: 'vehiclePhotosQuality',
    type: 'VEHICLE_PHOTOS_QUALITY'
  },
  REGO_CERTIFICATE_EXPIRY_MATCH: { key: 'expiry', type: 'REGO_EXPIRY' },
  REGO_CERTIFICATE_AUTHENTICITY: {
    key: 'registrationAuthenticity',
    type: 'REGO_CERTIFICATE'
  },
  BANK_STATEMENT_AUTHENTICITY: {
    key: 'bankAuthenticity',
    type: 'BANK_STATEMENT'
  },
  BANK_STATEMENT_ADDRESS_MATCH: {
    key: 'BANK_STATEMENT_ADDRESS',
    type: 'BANK_STATEMENT_ADDRESS'
  }
};
